import React, { Fragment } from 'react';
import NoImage from '../../../assets/no-image.svg';
import { Switch, Menu, Transition } from '@headlessui/react';
import {
  EllipsisVerticalIcon,
  PencilSquareIcon,
  CalendarDaysIcon,
  ReceiptPercentIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/20/solid';
import {
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  CursorArrowRaysIcon,
  BoltIcon,
  InformationCircleIcon,
} from '@heroicons/react/20/solid';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import AddOnImage from '../../../assets/add-on.svg';
import { Tooltip } from 'react-tooltip';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import TagBadge from '../../../views/tags/components/TagBadge';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { readPlatformFlags } from './platform-visibility';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function ProductCard({
  id,
  index,
  product,
  selected,
  setSelected,
  onChangeProductVisible,
  onClickEditProduct,
  onClickDuplicateProduct,
  storeTimezoneId,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: product.id });
  const hasNoItems = product.items.length === 0;
  const hasActiveHideDate =
    product.hidedate && new Date() < new Date(product.hidedate);
  const isClosed = product.isdisabled || !product.visible || hasActiveHideDate;
  const showDangerState = isClosed || hasNoItems;
  const platformFlags = readPlatformFlags(product.productsettings);

  const cardToneClass = showDangerState
    ? 'border-rose-300 bg-rose-50/90 text-rose-950 ring-1 ring-rose-200 dark:border-rose-500/70 dark:bg-rose-900/25 dark:text-rose-100 dark:ring-rose-500/20'
    : product.id === selected.product?.id
      ? 'border-blue-400 bg-blue-50/70 text-slate-900 ring-1 ring-blue-200 dark:border-sky-400 dark:bg-slate-700 dark:text-slate-100 dark:ring-sky-500/30'
      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-700/70 dark:text-slate-300 dark:hover:bg-slate-600';

  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={sortableStyle}
      {...attributes}
      {...listeners}
      key={id}
      onClick={() => {
        setSelected((prev) => ({
          ...prev,
          product: product,
          itemid: '',
          optiongroupid: '',
          optionid: '',
        }));
      }}
      onDoubleClick={() => onClickEditProduct(product)}
      className={classNames(
        cardToneClass,
        isDragging ? 'cursor-grabbing shadow-lg' : 'cursor-grab',
        'group mx-3 my-2 flex min-h-[5.75rem] items-center gap-4 rounded-md border px-4 py-3 transition-all duration-150',
      )}
    >
      <div className="relative inline-block">
        <img
          className="h-14 w-14 min-h-[3.5rem] min-w-[3.5rem] rounded-full object-cover ring-2 ring-white dark:ring-slate-700"
          src={product.imagepath || NoImage}
          alt=""
        />
        {product.isupsell && (
          <img
            className="absolute bottom-0 right-0 block h-6 w-6 rounded-full"
            alt="addOnImage"
            src={AddOnImage}
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div
          key={product.name}
          className={classNames(
            'flex cursor-pointer items-center text-sm font-medium',
          )}
          aria-current={
            product.id === selected.product?.id ? 'page' : undefined
          }
        >
          <div className="overflow-hidden min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-1.5 overflow-hidden">
              <span className="min-w-0 truncate font-semibold">
                {product.name}
              </span>
              {product.isdisabled && (
                <TagBadge
                  className="shrink-0"
                  text="Not Published"
                  textColor="white"
                  color="red"
                  size="xs"
                />
              )}
              {product.squaremissing && (
                <TagBadge
                  className="shrink-0"
                  text="Missing in Square"
                  textColor="white"
                  color="red"
                  size="xs"
                />
              )}
              {product.needsreview && (
                <TagBadge
                  className="shrink-0"
                  text="New from Square"
                  textColor="white"
                  color="#d97706"
                  size="xs"
                />
              )}
            </div>
            <p
              className={classNames(
                showDangerState
                  ? 'text-rose-600/90 dark:text-rose-300'
                  : product.id === selected.product?.id
                    ? 'text-slate-500 dark:text-slate-300'
                    : 'text-slate-400',
                'mt-0.5 overflow-hidden text-ellipsis whitespace-nowrap text-xs font-light',
              )}
            >
              {product.description}
            </p>
            <div className="mt-1 flex items-center gap-1 overflow-hidden">
              {platformFlags.isweb && (
                <ComputerDesktopIcon className="h-4 w-4 text-slate-500" />
              )}
              {platformFlags.ismobile && (
                <DevicePhoneMobileIcon className="h-4 w-4 text-slate-500" />
              )}
              {platformFlags.iskiosk && (
                <CursorArrowRaysIcon className="h-4 w-4 text-slate-500" />
              )}
              {product.isupsell && (
                <BoltIcon className="h-4 w-4 text-green-500" />
              )}
              {product.usecrvtax && (
                <InformationCircleIcon className="h-4 w-4 text-blue-500" />
              )}
              {product.tax !== 0 && product.usetax && (
                <ReceiptPercentIcon className="h-4 w-4 text-red-500" />
              )}
              {product.customproducttags?.map((customTag, index) => (
                <TagBadge
                  key={index}
                  text={customTag.tag.text}
                  textColor={customTag.tag.textcolor}
                  color={customTag.tag.color}
                  size="xxs"
                />
              ))}
              {isClosed && (
                <span className="shrink-0 inline-flex items-center rounded-full border border-rose-300 bg-rose-100 px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-[0.06em] text-rose-700 dark:border-rose-500/50 dark:bg-rose-900/30 dark:text-rose-200">
                  Closed
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      {hasActiveHideDate && (
        <>
          <Tooltip id="hidedate" />
          <div className="flex">
            <CalendarDaysIcon
              data-tooltip-id="hidedate"
              data-tooltip-content={`This product will be hidden on ${dayjs(
                product.hidedate,
              )
                .tz(storeTimezoneId || 'UTC')
                .format('MM/DD/YYYY hh:mm A')}`}
              className={classNames(
                'text-slate-500 z-10',
                'w-5 h-5 dark:text-sky-400',
              )}
            />
          </div>
        </>
      )}
      {!product.isdisabled && (
        <div
          className="pt-1"
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <Tooltip id="visibility" />
          <Switch
            key={index}
            data-tooltip-id="visibility"
            data-tooltip-content="It is used to change the visibility of the product in the menu (active - passive)."
            checked={product.visible}
            onChange={(val) => {
              onChangeProductVisible(product, val, index);
            }}
            className="group/switch relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full"
          >
            <span
              aria-hidden="true"
              className={classNames(
                showDangerState
                  ? 'bg-rose-100 dark:bg-rose-900/30'
                  : product.id === selected.product?.id
                    ? 'bg-blue-100 dark:bg-slate-600'
                    : 'bg-transparent',
                'pointer-events-none absolute h-full w-full rounded-md',
              )}
            />
            <span
              aria-hidden="true"
              className={classNames(
                hasActiveHideDate
                  ? 'bg-red-500'
                  : product.visible
                    ? 'bg-blue-600 dark:bg-sky-400'
                    : 'bg-red-500/80 dark:bg-rose-500',
                'pointer-events-none absolute mx-auto h-4 w-9 rounded-full transition-colors duration-200 ease-in-out',
              )}
            />
            <span
              aria-hidden="true"
              className={classNames(
                hasActiveHideDate
                  ? 'translate-x-0'
                  : product.visible
                    ? 'translate-x-5'
                    : 'translate-x-0',
                'pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border border-gray-200 dark:border-slate-700 bg-white shadow ring-0 transition-transform duration-200 ease-in-out',
              )}
            />
          </Switch>
        </div>
      )}
      <div
        className="flex items-center"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <Menu as="div" className="relative">
          <Menu.Button className="inline-flex items-center rounded-md p-1 text-slate-300 transition hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-200">
            <EllipsisVerticalIcon className="h-5 w-5" />
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 z-50 mt-1 w-36 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-slate-700 dark:ring-slate-600">
              <Menu.Item>
                {({ active }) => (
                  <button
                    className={classNames(
                      active ? 'bg-slate-50 dark:bg-slate-600' : '',
                      'flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200',
                    )}
                    onClick={() => onClickEditProduct(product)}
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                    Edit
                  </button>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <button
                    className={classNames(
                      active ? 'bg-slate-50 dark:bg-slate-600' : '',
                      'flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200',
                    )}
                    onClick={() => onClickDuplicateProduct(product)}
                  >
                    <DocumentDuplicateIcon className="h-4 w-4" />
                    Duplicate
                  </button>
                )}
              </Menu.Item>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    </div>
  );
}

export default ProductCard;
